import { FC, KeyboardEvent, useState, useMemo, useCallback } from 'react';
import { clsx } from 'clsx';
import { Menu, Typography, Stack, TextField, InputAdornment } from '@mui/material';
import { Search, Check, FolderOpenOutlined, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import './KanbanProjectSelector.scss';
import { ProjectFilterItem, useDropdownState } from '../../../hooks';
import { AddProjectBtn } from '../../Project/AddProjectBtn';
import { TODO_INTEGRATION_ICONS } from '../../TodoIntegration';

interface KanbanProjectSelectorProps {
  projects: ProjectFilterItem[];
  selectedProjects: ProjectFilterItem[];
  onProjectSelect: (projectId: string) => void;
  onProjectAdd?: () => void;
  enableToAdd?: boolean;
  disabled?: boolean;
  className?: string;
}

const KanbanProjectSelector: FC<KanbanProjectSelectorProps> = ({
  projects,
  selectedProjects,
  onProjectSelect,
  onProjectAdd,
  enableToAdd = false,
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const { anchorEl, handleOpenDropdown, handleCloseDropdown } = useDropdownState();
  const [searchQuery, setSearchQuery] = useState<string>('');

  const selectedProjectIds = selectedProjects.map((p) => p.todoTaskId);
  const isProjectSelected = (projectId: string) => selectedProjectIds.includes(projectId);

  const hasSelectedProjects =
    selectedProjects.length > 0 &&
    selectedProjects.length < projects.length &&
    !selectedProjectIds.includes('all');

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenDropdown(event as any);
    }
  };

  const handleProjectAdd = () => {
    if (onProjectAdd) {
      onProjectAdd();
      handleCloseDropdown();
    }
  };

  const filteredProjects = useMemo(
    () =>
      projects.filter((project) => project.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [projects, searchQuery]
  );

  const handleProjectClick = useCallback(
    (projectId: string) => {
      onProjectSelect(projectId);
    },
    [onProjectSelect]
  );

  return (
    <>
      <Stack
        className={clsx(
          'kanban-project-selector',
          className,
          anchorEl && 'kanban-project-selector--open',
          hasSelectedProjects && 'kanban-project-selector--selected',
          disabled && 'kanban-project-selector--disabled'
        )}
        onClick={disabled ? undefined : handleOpenDropdown}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        role="button"
        aria-label={t('task.filters.projectFilter')}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleTriggerKeyDown}
      >
        <FolderOpenOutlined className="kanban-project-selector__icon" />
        <ExpandMore className="kanban-project-selector__arrow" />
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          handleCloseDropdown();
          setSearchQuery('');
        }}
        className="kanban-project-selector__dropdown"
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        MenuListProps={{
          onMouseDown: (e) => e.preventDefault(),
        }}
      >
        <div className="kanban-project-selector__dropdown__header">
          <Typography variant="body2" className="kanban-project-selector__dropdown__header__text">
            {t('task.filters.filterByProjects')}
          </Typography>
        </div>
        <div
          className="kanban-project-selector__dropdown__search"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <TextField
            size="small"
            placeholder={t('task.filters.filterProjects')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            className="kanban-project-selector__dropdown__search__input"
          />
        </div>
        {filteredProjects.length > 0 ? (
          filteredProjects.map((project) => {
            const isSelected = isProjectSelected(project.todoTaskId);
            return (
              <div
                key={project.todoTaskId}
                onClick={() => handleProjectClick(project.todoTaskId)}
                className={clsx('kanban-project-selector__dropdown__item', {
                  'kanban-project-selector__dropdown__item--selected': isSelected,
                })}
              >
                {project.appFrom === 'all' ? (
                  <span className="kanban-project-selector__dropdown__item__icon kanban-project-selector__dropdown__item__icon--all">
                    #
                  </span>
                ) : (
                  <img
                    src={TODO_INTEGRATION_ICONS[project.appFrom]}
                    alt={project.appFrom}
                    className="kanban-project-selector__dropdown__item__icon"
                  />
                )}
                <Typography
                  variant="body2"
                  className="kanban-project-selector__dropdown__item__text"
                >
                  {project.name}
                </Typography>
                {isSelected && (
                  <Check
                    fontSize="small"
                    className="kanban-project-selector__dropdown__item__check"
                  />
                )}
              </div>
            );
          })
        ) : (
          <div className="kanban-project-selector__dropdown__no-results">
            <Typography
              variant="body2"
              className="kanban-project-selector__dropdown__no-results__text"
            >
              {t('task.filters.noProjects')}
            </Typography>
            <Typography
              variant="caption"
              className="kanban-project-selector__dropdown__no-results__subtext"
            >
              {t('task.filters.tryDifferentQuery')}
            </Typography>
          </div>
        )}
        {enableToAdd && onProjectAdd && (
          <AddProjectBtn
            onClick={handleProjectAdd}
            className="kanban-project-selector__dropdown__add-project"
          />
        )}
      </Menu>
    </>
  );
};

export default KanbanProjectSelector;
